(() => {
  let cardSequence = 0;
  if (!customElements.get('es-hover-gallery')) {
    class HoverGallery extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.link = this.querySelector('a');
        this.images = [...this.querySelectorAll('a > img')];
        this.dots = [...this.querySelectorAll('.es-product-card__dots span')];
        this.run = 0;
        this.index = 0;
        const hoverTarget = this.closest('es-best-card') || this;
        hoverTarget.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') this.start();
        }, options);
        hoverTarget.addEventListener('pointerleave', () => this.stop(), options);
        this.closest('.swiper')?.addEventListener('pointerdown', () => this.stop(), options);
        window.addEventListener('blur', () => this.stop(), options);
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, options);
        this.motion.addEventListener('change', () => this.stop(), options);
        this.pointer.addEventListener('change', () => this.stop(), options);
        this.closest('es-best-carousel')?.addEventListener('es:slide-change', () => this.stop(), options);
        this.observer = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting) this.stop();
        });
        this.observer.observe(this);
      }
      start() {
        if (this.dataset.enabled !== 'true' || !this.pointer.matches || this.motion.matches || document.hidden || this.active) return;
        const template = this.querySelector('template');
        if (template) {
          this.link.append(template.content.cloneNode(true));
          template.remove();
          this.images = [...this.querySelectorAll('a > img')];
        }
        if (this.images.length < 2) return;
        this.active = true;
        this.run += 1;
        this.schedule(this.run);
      }
      schedule(run) {
        this.timer = setTimeout(async () => {
          this.timer = null;
          if (!this.active || run !== this.run) return;
          this.index = (this.index + 1) % this.images.length;
          const next = this.images[this.index];
          if (next.dataset.src) {
            next.srcset = next.dataset.srcset;
            next.src = next.dataset.src;
            delete next.dataset.src;
            delete next.dataset.srcset;
          }
          try { await next.decode(); } catch (_) { /* Keep the current image on a failed request. */ }
          if (!this.isConnected || !this.active || run !== this.run) return;
          if (next.naturalWidth) this.show(this.index);
          this.schedule(run);
        }, 1100);
      }
      show(index) {
        this.images.forEach((image, i) => {
          image.classList.toggle('is-active', i === index);
          image.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      }
      stop() {
        clearTimeout(this.timer);
        this.timer = null;
        this.active = false;
        this.run += 1;
        this.index = 0;
        this.show(0);
      }
      disconnectedCallback() {
        this.stop();
        this.controller?.abort();
        this.observer?.disconnect();
      }
    }
    customElements.define('es-hover-gallery', HoverGallery);
  }

  if (!customElements.get('es-best-card')) {
    customElements.define('es-best-card', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.controller = new AbortController();
        const options = {signal: this.controller.signal};
        this.querySelectorAll('form[id]').forEach(form => { form.setAttribute('id', `${form.getAttribute('id').replace(/-instance-\d+$/, '')}-instance-${++cardSequence}`); });
        this.addEventListener('keyup', event => {
          if (event.target.closest('button, [role="button"]') && ['Space', 'Enter'].includes(event.code)) event.stopPropagation();
        }, options);
        this.querySelectorAll('.product__color-swatches--js').forEach(swatch => {
          swatch.tabIndex = 0;
          swatch.setAttribute('role', 'button');
          swatch.setAttribute('aria-label', swatch.dataset.value);
          swatch.addEventListener('keydown', event => {
            if (['Space', 'Enter'].includes(event.code)) { event.preventDefault(); swatch.click(); }
          }, options);
        });
        this.addEventListener('click', event => {
          const swatch = event.target.closest('.product__color-swatches--js');
          if (!swatch) return;
          const variant = swatch.closest('variant-radios')?.currentVariant;
          if (!variant) return;
          const discount = variant.compare_at_price > variant.price ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
          this.querySelectorAll('.es-product-card__badge--sale, .es-product-card__saving').forEach(badge => {
            badge.hidden = !discount;
            badge.textContent = badge.classList.contains('es-product-card__saving') ? `Save ${discount}%` : `-${discount}%`;
          });
          const stockBadge = this.querySelector('[data-stock-badge]');
          const preorderBadge = this.querySelector('[data-preorder-badge]');
          if (stockBadge) stockBadge.hidden = variant.available;
          if (preorderBadge) preorderBadge.hidden = !(variant.available && variant.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue');
          this.querySelectorAll('.es-product-card__title[href], .es-product-card__image-link[href]').forEach(link => {
            const url = new URL(link.href); url.searchParams.set('variant', variant.id); link.href = url.href;
          });
          this.querySelectorAll('select-option, button-quickview').forEach(control => {
            const url = new URL(control.dataset.url, location.origin); url.searchParams.set('variant', variant.id);
            control.dataset.url = url.pathname + url.search; control.url = control.dataset.url;
          });
          const gallery = this.querySelector('es-hover-gallery');
          if (gallery) { gallery.stop(); gallery.dataset.enabled = 'false'; }
        }, options);
        const wishlist = this.querySelector('button-wishlist');
        const compare = this.querySelector('button-compare');
        const update = () => {
          const button = wishlist?.querySelector('button');
          if (button) {
            const active = wishlist.classList.contains('active');
            button.setAttribute('aria-pressed', String(active));
            button.setAttribute('aria-label', active ? (wishlist.dataset.action === 'remove' ? 'Remove from wishlist' : 'Browse wishlist') : 'Add to wishlist');
          }
          compare?.querySelector('button')?.setAttribute('aria-pressed', String(compare.classList.contains('active')));
          this.querySelectorAll('.product__color-swatches--js').forEach(swatch => swatch.setAttribute('aria-pressed', String(swatch.classList.contains('active'))));
          this.querySelectorAll('.es-product-card__cta').forEach(button => {
            button.setAttribute('aria-busy', String(button.classList.contains('loading') || button.parentElement.classList.contains('loading')));
          });
        };
        this.observer = new MutationObserver(update);
        [wishlist, compare, ...this.querySelectorAll('select-option, button-quickview, [type="submit"], .product__color-swatches--js')].filter(Boolean).forEach(element => this.observer.observe(element, {attributes: true, attributeFilter: ['class']}));
        update();
      }
      disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); }
    });
  }

  customElements.whenDefined('slide-section').then(() => {
    if (customElements.get('es-best-carousel')) return;
    customElements.define('es-best-carousel', class extends customElements.get('slide-section') {
      // Initialize once when connected, including section replacements in the editor.
      init() {}
      connectedCallback() {
        queueMicrotask(() => {
          if (!this.isConnected || this.globalSlide) return;
          super.initSlide();
          const slider = this.globalSlide;
          const columns = Number(this.dataset.desktop) || 5;
          const gap = Number(this.dataset.spacing) || 16;
          const breakpoints = {
            768: {slidesPerView: Math.min(columns, 3), spaceBetween: gap},
            1025: {slidesPerView: Math.min(columns, 4), spaceBetween: gap},
            1400: {slidesPerView: columns, spaceBetween: gap}
          };
          slider.params.breakpoints = breakpoints;
          slider.originalParams.breakpoints = breakpoints;
          slider.currentBreakpoint = null;
          slider.setBreakpoint();
          slider.update();
          slider.on('slideChangeTransitionStart', () => this.dispatchEvent(new Event('es:slide-change')));
          this.controller = new AbortController();
          // The theme's document search handler cancels every keyup, including Space.
          // Keep native button activation within this section out of that handler.
          this.addEventListener('keyup', event => {
            if (event.target.closest('button') && ['Space', 'Enter'].includes(event.code)) event.stopPropagation();
          }, {signal: this.controller.signal});
          this.motion = matchMedia('(prefers-reduced-motion: reduce)');
          const motionChange = () => { if (this.motion.matches) slider.autoplay?.stop(); };
          this.motion.addEventListener('change', motionChange, {signal: this.controller.signal});
          motionChange();
        });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.globalSlide?.destroy(true, true);
        this.globalSlide = null;
      }
    });
  });
})();
